import { Fragment } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { Text, Popover, usePopoverOpen, zIndex, colors } from '@spaceshipapp/cambridge';
import Button from '../../components/Button';
import Link from '../../components/Link';

import logo from '../../images/logo-m.svg';

import useAuth from '../../contexts/auth';
import { trans } from '../../utils/helpers';
import { Description } from './styled';
import { referralPageAttempt } from '../../analytics/referral';
import Notification from '../../components/Notification';
import Trans from 'next-translate/Trans';

const NAV_HEIGHT = '75px';
const REFERRAL_ENABLED = process.env.NEXT_PUBLIC_REFERRAL_ENABLED === 'TRUE';

const MobileHeader: React.FC = () => {
  const { t } = useTranslation('common');
  const { pathname = '' } = useRouter() || {};
  const shouldShowPeakSeasonWarning = false;

  return (
    <>
      <Header>
        <Container className="container">
          <Wrapper>
            <Link
              href="/ship"
              aria-label={t('Header.logo')}
              data-tracking-label={t('commonEn:Header.logo')}
            >
              <img src={logo} alt={t('Header.logo')} width={32} height={32} />
            </Link>
          </Wrapper>
          <Wrapper>
            <Popover
              trigger={
                <Button
                  iconName="Globe"
                  size="sm"
                  variant="link"
                  aria-label={t('Header.language')}
                  data-tracking-label={t('commonEn:Header.language')}
                />
              }
            >
              <Languages />
            </Popover>
            <Popover
              trigger={
                <Button
                  iconName="Menu"
                  variant="link"
                  size="sm"
                  aria-label={t('Header.menu')}
                  data-tracking-label={t('commonEn:Header.menu')}
                />
              }
            >
              <NavMenu />
            </Popover>
          </Wrapper>
        </Container>
      </Header>
      {shouldShowPeakSeasonWarning && (
        <Notification
          variant="warning"
          message={
            <Trans
              i18nKey={t('common:ShipmentInfo.peakSeasonWarning')}
              components={{
                strong: <strong />,
                /* eslint-disable jsx-a11y/anchor-has-content */
                /* eslint-disable @typescript-eslint/naming-convention */
                1: (
                  <a
                    href={t('common:ShipmentInfo.peakSeasonWarningSupportUrl')}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Peak Season Support Article"
                  />
                ),
                /* eslint-enable jsx-a11y/anchor-has-content */
                /* eslint-enable @typescript-eslint/naming-convention */
              }}
            />
          }
        />
      )}
    </>
  );
};

const Languages = () => {
  const { asPath, locales = [] } = useRouter();
  const togglePopover = usePopoverOpen();

  const handleClose = () => {
    togglePopover(false);
  };

  return (
    <Menu onClick={handleClose}>
      <div className="container">
        <ul>
          {locales.map((locale) => (
            <li key={locale}>
              <Link href={asPath} locale={locale}>
                {trans[locale]}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Menu>
  );
};

const NavMenu: React.FC = () => {
  const { t } = useTranslation('common');
  const [{ authenticated, user }] = useAuth();
  const togglePopover = usePopoverOpen();

  const handleClose = () => {
    togglePopover(false);
  };

  return (
    <Menu onClick={handleClose}>
      <Nav>
        <div className="container">
          <ul>
            <li>
              <Link href="/ship" data-tracking-label={t('commonEn:Header.shipping')}>
                {t('Header.shipping')}
              </Link>
            </li>
            <li>
              <Link href="/orders" data-tracking-label={t('commonEn:Header.shipping')}>
                {t('Header.tracking')}
              </Link>
            </li>
            {REFERRAL_ENABLED && (
              <li>
                <Link
                  onClick={() => {
                    referralPageAttempt(authenticated);
                  }}
                  href="/referral"
                  data-tracking-label={t('commonEn:Header.referral')}
                >
                  <p>{t('Header.referral')}</p>
                  <Description>{t('Header.referralDescription')}</Description>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </Nav>
      <BottomSheet>
        <div className="container">
          {authenticated && (
            <Profile>
              <Text className={usernameStyle}>{user?.name}</Text>
              <Text className="subtext" size="sm">
                {user?.email}
              </Text>
            </Profile>
          )}
          <Auth>
            {authenticated ? (
              <Button
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore: the "as" prop for linaria
                as={Link}
                href="/logout"
                size="sm"
                block
                data-tracking-label={t('commonEn:Header.signOut')}
              >
                {t('Header.signOut')}
              </Button>
            ) : (
              <Fragment>
                <Button
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore: the "as" prop for linaria
                  as={Link}
                  passHref
                  href="/login"
                  size="sm"
                  block
                  data-tracking-label={t('commonEn:Header.signIn')}
                >
                  {t('Header.signIn')}
                </Button>
                <Button
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore: the "as" prop for linaria
                  as={Link}
                  passHref
                  href="/register"
                  size="sm"
                  block
                  data-tracking-label={t('commonEn:Header.register')}
                >
                  {t('Header.register')}
                </Button>
              </Fragment>
            )}
          </Auth>
        </div>
      </BottomSheet>
    </Menu>
  );
};

const Header = styled.header`
  position: sticky;
  z-index: ${zIndex.HEADER};
  top: 0;
  display: flex;
  width: 100%;
  padding: 1em 0;
  background-color: ${colors.brandSecondary};
  border-bottom: 1px solid ${colors.border};
  height: ${NAV_HEIGHT};

  a {
    text-decoration: none;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  > *:not(:first-child) {
    margin-left: 0.5rem;
  }
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: ${NAV_HEIGHT};
  right: 0;
  left: 0;
  bottom: 0;
  background-color: ${colors.brandSecondary};
  overflow: auto;

  a {
    text-decoration: none;
  }

  li {
    font-size: 1.5em;
    margin: 2rem 0;
    padding: 0 0.5rem;

    a {
      display: block;
      width: 100%;

      &.active {
        position: relative;
        font-weight: 600;

        &:before {
          position: absolute;
          content: '';
          width: 0.25rem;
          height: 100%;
          margin-left: -1.5rem;
          background-color: ${colors.text};
        }
      }
    }
  }
`;

const Nav = styled.nav`
  flex-grow: 1;
`;

const BottomSheet = styled.div`
  border-top: 1px solid ${colors.border};
  width: 100%;
  padding-top: 1rem;
  padding-bottom: calc(1rem + var(--safe-area-inset-bottom));
`;

const Profile = styled.div`
  margin-bottom: 1rem;
`;

const Auth = styled.div`
  display: flex;
  justify-content: space-between;

  > *:not(:first-child) {
    margin-left: 1.5rem;
  }
`;

const usernameStyle = css`
  font-weight: 600;
`;

export default MobileHeader;
